/**
 * Sub-account management — tap a sub-account to switch wallet view (ys142857).
 */
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { subAccountsAPI } from '../api/sub-accounts';
import { usersAPI } from '../api/users';
import { ApiError } from '../api/client';
import { useAuthStore } from '../store/auth';
import { useActiveAccountStore } from '../store/activeAccount';
import { SubAccountListCard, type SubAccountListItem } from '../components/sub-accounts/SubAccountListCard';
import {
  SubAccountImputationModal,
  formatImputationTotals,
  type ImputationTotals,
} from '../components/sub-accounts/SubAccountImputationModal';
import { EmptyState } from '../components/ui/EmptyState';
import { LoadingSkeleton } from '../components/ui/LoadingSkeleton';
import { ThemedPageHeader } from '../components/ui/ThemedPageHeader';
import {
  THEMED_SURFACE_COLORS as C,
  ThemedBottomActionBar,
  ThemedChip,
  ThemedMetric,
  ThemedPanel,
  ThemedSubmitButton,
} from '../components/ui/ThemedSurface';

function RelationSummaryPanel({ username, total, page, totalPages }: { username?: string; total: number; page: number; totalPages: number }) {
  const { t } = useTranslation();

  return (
    <ThemedPanel className="p-0">
      <div className="relative px-4 py-4">
        <div className="pointer-events-none absolute inset-x-4 top-0 h-px" style={{ background: `linear-gradient(90deg, transparent 0%, ${C.gold} 50%, transparent 100%)` }} />
        <div className="flex items-start justify-between gap-3">
          <div className="truncate text-base font-semibold" style={{ color: C.text }}>
            {username || '—'} {t('subAccounts.relation')}
          </div>
        </div>

        <div className="mt-4 grid grid-cols-[1fr_auto_1fr] items-center rounded-xl border px-3 py-3" style={{ borderColor: C.softBorder, background: C.inputBg }}>
          <ThemedMetric label={t('common.total')} value={`${total} ${t('subAccounts.accounts')}`} color={C.gold} />
          <div className="mx-3 h-10 w-px" style={{ background: C.statDividerAlt }} />
          <ThemedMetric label={t('subAccounts.page')} value={`${page} / ${Math.max(totalPages, 1)}`} color={C.text} />
        </div>
      </div>
    </ThemedPanel>
  );
}

function MasterAccountPanel({ masterData }: { masterData?: { id: number | string; username?: string; phone?: string } }) {
  const { t } = useTranslation();
  const initial = (masterData?.username?.trim()?.[0] ?? 'M').toUpperCase();
  const setActiveAccount = useActiveAccountStore((s) => s.setActiveAccount);
  const activeId = useActiveAccountStore((s) => s.accountId);
  const navigate = useNavigate();

  return (
    <ThemedPanel className="p-0">
      <div className="relative px-4 py-4">
        <div className="mb-3 flex items-center justify-between gap-3">
          <div className="text-sm font-semibold" style={{ color: C.text }}>{t('subAccounts.master')}</div>
          {masterData ? <ThemedChip active>ID: {masterData.id}</ThemedChip> : null}
        </div>

        {masterData ? (
          <button
            type="button"
            onClick={() => {
              setActiveAccount(null, null);
              navigate('/wallet');
            }}
            className="flex w-full items-center gap-3 rounded-xl border px-3 py-3 text-left transition-all active:scale-[0.98]"
            style={{
              borderColor: activeId === null ? `${C.gold}66` : C.softBorder,
              background: activeId === null ? C.chipBg : C.inputBg,
            }}
          >
            <div className="flex size-11 shrink-0 items-center justify-center rounded-xl border text-lg font-semibold" style={{ borderColor: `${C.gold}66`, background: C.chipBg, color: C.gold, boxShadow: `0 0 18px ${C.gold}18` }}>
              {initial}
            </div>
            <div className="min-w-0 flex-1">
              <div className="truncate text-lg font-bold leading-none" style={{ color: C.text }}>{masterData.username || '—'}</div>
              <div className="mt-2 flex items-center gap-2 text-xs" style={{ color: C.muted }}>
                <span className="h-1.5 w-1.5 rounded-full" style={{ background: C.gold }} />
                <span className="truncate">{masterData.phone || '—'}</span>
              </div>
            </div>
          </button>
        ) : (
          <div className="rounded-xl border px-3 py-3 text-sm" style={{ borderColor: C.softBorder, background: C.inputBg, color: C.muted }}>
            {t('subAccounts.noMaster')}
          </div>
        )}
      </div>
    </ThemedPanel>
  );
}

export function SubAccounts() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const profile = useAuthStore((s) => s.profile) as Record<string, unknown> | null;
  const activeId = useActiveAccountStore((s) => s.accountId);
  const setActiveAccount = useActiveAccountStore((s) => s.setActiveAccount);
  const [page, setPage] = useState(1);
  const [imputeOpen, setImputeOpen] = useState(false);
  const [imputeError, setImputeError] = useState<string | null>(null);
  const [imputeToast, setImputeToast] = useState<string | null>(null);
  const limit = 20;

  const { data: masterData } = useQuery({
    queryKey: ['sub-accounts-master'],
    queryFn: async () => subAccountsAPI.getMasterAccount(),
    staleTime: 60_000,
  });

  const { data: subsData, isLoading } = useQuery({
    queryKey: ['sub-accounts-list', page],
    queryFn: async () => subAccountsAPI.getSubAccounts({ page, limit }),
    staleTime: 30_000,
  });

  const subs = (subsData?.rows ?? []) as SubAccountListItem[];
  const total = subsData?.total ?? 0;
  const totalPages = Math.ceil(total / limit);

  const imputeMut = useMutation({
    mutationFn: (body: { transaction_password: string; security_answer: string }) =>
      usersAPI.imputation({
        impute_all: true,
        asset_filter: 'all',
        transaction_password: body.transaction_password,
        security_answer: body.security_answer,
      }) as Promise<ImputationTotals & { ref?: string }>,
    onSuccess: (data) => {
      setImputeOpen(false);
      setImputeError(null);
      const summary = formatImputationTotals(data);
      setImputeToast(
        summary
          ? `${t('subAccounts.imputationDone', '归集完成')}${summary}`
          : t('imputation.successMsg'),
      );
      void queryClient.invalidateQueries({ queryKey: ['sub-accounts-list'] });
      void queryClient.invalidateQueries({ queryKey: ['sub-accounts-master'] });
      void queryClient.invalidateQueries({ queryKey: ['wallet-balances'] });
      setTimeout(() => setImputeToast(null), 4000);
    },
    onError: (e: unknown) => {
      setImputeError(e instanceof ApiError ? e.message : t('imputation.submitFailed'));
    },
  });

  function selectSub(sub: SubAccountListItem) {
    setActiveAccount(sub.id, sub.username);
    navigate('/wallet');
  }

  function openImputation() {
    if (total <= 0) return;
    setImputeError(null);
    setImputeOpen(true);
  }

  return (
    <div className="relative min-h-full" style={{ background: C.pageBg }}>
      <div className="relative mx-auto min-h-full w-full max-w-[430px]">
        <ThemedPageHeader title={t('subAccounts.title')} onBack={() => navigate(-1)} theme={C} />
        <div className="flex flex-col gap-4 overflow-y-auto px-5 pt-[70px]" style={{ height: 'calc(100% - 70px)', paddingBottom: '96px' }}>
          <RelationSummaryPanel username={profile?.username as string | undefined} total={total} page={page} totalPages={totalPages} />

          <MasterAccountPanel masterData={masterData ?? undefined} />

          <ThemedPanel className="p-4">
            <div className="mb-3 flex items-center justify-between gap-3">
              <div className="text-sm font-semibold" style={{ color: C.text }}>{t('subAccounts.mine')}</div>
              <ThemedChip>{total} {t('subAccounts.accounts')}</ThemedChip>
            </div>
            <div className="mb-3 flex flex-col gap-2 sm:flex-row">
              <button
                type="button"
                onClick={() => navigate('/open-sub-account')}
                className="flex-1 rounded-lg border px-3 py-2 text-[11px] font-semibold transition active:scale-[0.98]"
                style={{ borderColor: `${C.gold}88`, color: C.gold, background: `${C.gold}14` }}
              >
                {t('subAccounts.registerSubAccount', '注册子账户')}
              </button>
              <button
                type="button"
                onClick={() => navigate('/register-member')}
                className="flex-1 rounded-lg border px-3 py-2 text-[11px] font-semibold transition active:scale-[0.98]"
                style={{ borderColor: `${C.gold}88`, color: C.gold, background: `${C.gold}14` }}
              >
                {t('subAccounts.registerNewPlayer', '注册新玩家')}
              </button>
            </div>
            <div className="mb-3 text-xs" style={{ color: C.muted }}>
              {t('subAccounts.tapToSwitch', '点击子账户切换钱包资产显示')}
            </div>
            {isLoading && (
              <div className="flex flex-col gap-3">
                <LoadingSkeleton className="h-36" />
                <LoadingSkeleton className="h-36" />
                <LoadingSkeleton className="h-36" />
              </div>
            )}
            {!isLoading && subs.length === 0 && <EmptyState title={t('subAccounts.noSubs')} />}
            {!isLoading && subs.length > 0 && (
              <div className="flex flex-col gap-3">
                {subs.map((sub) => (
                  <SubAccountListCard
                    key={sub.id}
                    sub={sub}
                    active={activeId === sub.id}
                    onSelect={() => selectSub(sub)}
                  />
                ))}
              </div>
            )}
            {totalPages > 1 && (
              <div className="mt-4 flex items-center justify-center gap-3">
                <button type="button" disabled={page <= 1} onClick={() => setPage((p) => p - 1)} className="rounded-lg border px-3 py-2 text-xs font-semibold disabled:opacity-50" style={{ borderColor: C.softBorder, background: C.inputBg, color: C.text }}>
                  {t('common.prevPage')}
                </button>
                <span className="text-sm" style={{ color: C.muted }}>{page} / {totalPages}</span>
                <button type="button" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)} className="rounded-lg border px-3 py-2 text-xs font-semibold disabled:opacity-50" style={{ borderColor: C.softBorder, background: C.inputBg, color: C.text }}>
                  {t('common.nextPage')}
                </button>
              </div>
            )}
          </ThemedPanel>

          {imputeToast ? (
            <div
              className="fixed left-1/2 top-24 z-50 max-w-[90%] -translate-x-1/2 rounded-xl border px-4 py-3 text-center text-xs font-medium shadow-lg"
              style={{ borderColor: `${C.gold}66`, background: '#222D3F', color: C.gold }}
            >
              {imputeToast}
            </div>
          ) : null}
        </div>

        <ThemedBottomActionBar background="#131F2F">
          <ThemedSubmitButton disabled={total <= 0 || imputeMut.isPending} onClick={openImputation}>
            {t('subAccounts.imputationCta', '一键归集')}
          </ThemedSubmitButton>
        </ThemedBottomActionBar>

        <SubAccountImputationModal
          open={imputeOpen}
          pending={imputeMut.isPending}
          error={imputeError}
          onClose={() => {
            if (!imputeMut.isPending) setImputeOpen(false);
          }}
          onConfirm={(payload) => imputeMut.mutate(payload)}
        />
      </div>
    </div>
  );
}

export default SubAccounts;
